import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  SupplierComplianceDocument,
  supplierComplianceDocumentsApi,
} from '@texas/api/endpoints/supplierComplianceDocumentsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { supplierComplianceDocumentEvents } from './events';

export function useSupplierComplianceDocumentBranches(
  document: SupplierComplianceDocument,
) {
  const { t } = useTranslation();
  const toast = useToast();
  const disclosure = useValueDisclosure<number[]>();
  const selectedBranchIds = disclosure.value ?? [];
  const {
    request: updateBranches,
    loading,
    error,
  } = useApiRequest(supplierComplianceDocumentsApi.updateBranches);
  const currentBranchIds = document.branches.map((branch) => branch.id);
  const hasChanges =
    selectedBranchIds.length !== currentBranchIds.length ||
    selectedBranchIds.some((branchId) => !currentBranchIds.includes(branchId));

  const open = () => {
    disclosure.onOpen(currentBranchIds);
  };

  const selectBranches = (values: string | string[]) => {
    const branchIds = Array.isArray(values) ? values : [values];
    disclosure.onOpen(branchIds.map(Number));
  };

  const apply = async () => {
    const response = await updateBranches(document.id, {
      branchIds: selectedBranchIds,
    });
    if (response.hasError) return;

    toast({
      title: t('general.updated'),
      status: 'success',
      isClosable: true,
    });
    supplierComplianceDocumentEvents.changed.dispatch(document.supplierId);
    disclosure.onClose();
  };

  return {
    apply,
    close: disclosure.onClose,
    error,
    hasChanges,
    isOpen: disclosure.isOpen,
    loading,
    open,
    selectedBranchIds,
    selectBranches,
  };
}
